/**
 * STC (Solde de Tout Compte) types.
 */

export interface STCEmployeeInfo {
  id: number;
  matricule: string;
  nom_complet: string;
  cin: string | null;
  numero_cnss: string | null;
  fonction: string | null;
  date_sortie: string | null;
  motif_sortie: string | null;
  // Used to prefill a new STC (all suggestions remain editable).
  salaire_base?: number | null;
  date_embauche?: string | null;
}

/** The 16 manual gain fields + the two retenue bases, in model order. */
export interface STCGains {
  salaire_base: number;
  jours_feries: number;
  conges_payes_stc: number;
  rappel_salaire: number;
  prime_anciennete: number;
  prime_tournee: number;
  prime_encadrement: number;
  indemnite_salissure: number;
  indemnite_representation: number;
  indemnite_panier: number;
  indemnite_transport: number;
  indemnite_lait: number;
  prime_responsabilite: number;
  indemnite_licenciement: number;
  prime_exceptionnelle: number;
  dommages_interets: number;
  cnss_base: number;
  amo_base: number;
}

/**
 * One dynamic gain line — an actual prime of the employee, prorated at exit.
 * Frozen into the STC on save so an issued receipt never changes when HR later
 * edits the employee's prime configuration.
 */
export interface STCPrimeLine {
  libelle: string;
  montant: number;
  /**
   * Un-prorated amount `montant` was derived from, so the form can show
   * "base × jours/26 = montant". Null on hand-typed lines and on receipts
   * issued before this field existed.
   */
  montant_base?: number | null;
  prime_id?: number | null;
  type?: 'exoneree' | 'non_exoneree' | 'manuel';
  prorated?: boolean;
}

/**
 * How the prorated gains were computed, frozen with the receipt so reopening it
 * replays the same arithmetic instead of re-deriving it from pointage that may
 * have changed since. All optional — receipts issued before this show no formula.
 */
export interface STCProration {
  proration_jours?: number | null;       // worked days, numerator over 26
  proration_facteur?: number | null;     // min(heures réelles / 191, 1)
  salaire_base_complet?: number | null;  // salary before proration
  taux_anciennete?: number | null;       // seniority rate, e.g. 0.05
}

/** Denominator of the proration fraction — a full month is 26 worked days. */
export const STC_JOURS_MOIS = 26;

/** Pointage-based proration + prime suggestions for a NEW STC (all editable). */
export interface STCPrefill {
  date_debut: string;
  date_fin: string;
  has_pointage: boolean;
  facteur: number;
  heures_presence: number;
  jours_ferie: number;
  jours_conge: number;
  heures_reelles: number;
  jours_travailles: number;   // heures réelles ÷ (191/26) — numerator over 26
  salaire_base_complet: number;
  salaire_base_prorate: number;
  prime_anciennete: number;   // computed backend-side (prorated salary × taux)
  taux_anciennete: number;    // the rate behind prime_anciennete, e.g. 0.05
  acompte_salaire: number;    // outstanding advances (Prêts + VALIDEE acomptes)
  primes_lines: STCPrimeLine[];
}

export interface STC extends STCGains, STCProration {
  id: number;
  ref_stc: string | null;
  employee_id: number;
  date_etablissement: string;
  mode_reglement: string | null;
  primes_lines: STCPrimeLine[];
  created_at: string;
  updated_at: string;
  // Derived (computed backend-side)
  cnss_montant: number;
  amo_montant: number;
  acompte_salaire: number;
  total_brut: number;
  total_retenues: number;
  net_a_payer: number;
  employee: STCEmployeeInfo | null;
}

export interface STCCreate extends STCGains, STCProration {
  employee_id: number;
  date_etablissement: string;
  mode_reglement?: string;
  primes_lines?: STCPrimeLine[];
}

export interface STCUpdate extends Partial<STCGains>, STCProration {
  date_etablissement?: string;
  mode_reglement?: string;
  primes_lines?: STCPrimeLine[];
}

export interface STCListResponse {
  items: STC[];
  total: number;
}

/** Ordered [field, label] for rendering the gain inputs — matches the receipt. */
export const STC_GAIN_ROWS: { key: keyof STCGains; label: string }[] = [
  { key: 'salaire_base', label: 'Salaire de base' },
  { key: 'jours_feries', label: 'Jours fériés' },
  { key: 'conges_payes_stc', label: 'Congés payés STC' },
  { key: 'rappel_salaire', label: 'Rappel sur salaire' },
  { key: 'prime_anciennete', label: "Prime d'ancienneté" },
  { key: 'prime_tournee', label: 'Prime de Tournee' },
  { key: 'prime_encadrement', label: "Prime d'encadrement" },
  { key: 'indemnite_salissure', label: 'Indemnité de Salissure' },
  { key: 'indemnite_representation', label: 'Indemnité de Représentation' },
  { key: 'indemnite_panier', label: 'Indemnité de Panier' },
  { key: 'indemnite_transport', label: 'Indemnité de Transport' },
  { key: 'indemnite_lait', label: 'Indemnité de lait' },
  { key: 'prime_responsabilite', label: 'Prime de responsabilité' },
  { key: 'indemnite_licenciement', label: 'Indemnité de licenciement' },
  { key: 'prime_exceptionnelle', label: 'Prime exceptionnelle' },
  { key: 'dommages_interets', label: 'Dommages intérêts' },
];

/**
 * Fixed prime/indemnité columns kept only for STCs created before the dynamic
 * `primes_lines`. The form hides them unless an existing record has a value —
 * new STCs get one line per prime the employee actually has instead.
 */
export const STC_LEGACY_PRIME_KEYS: (keyof STCGains)[] = [
  'prime_tournee',
  'prime_encadrement',
  'indemnite_salissure',
  'indemnite_representation',
  'indemnite_panier',
  'indemnite_transport',
  'indemnite_lait',
  'prime_responsabilite',
];
